import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { parseCsvTable } from "@/utils/parseCsvText";

import { ParsedXlsxData } from "../../../CsvEnrichment/components/titleGate/titleGate.utils";
import { CsvEnrichment } from "../../../CsvEnrichment/schema/csvEnrichment";
import { List } from "../../schema/list";

export interface FieldMappingEntry {
	foundField: string;
	assignedField: string;
}

export interface MappingOption {
	title: string;
	id: string;
}

/** The system fields a CSV column can be mapped to, per list entity type. */
export const mappingOptionsFor = (entityType?: List["entityType"]): MappingOption[] => {
	if (entityType === CsvEnrichmentType.ORGANIZATION) {
		return [
			{ title: "Do Not import", id: "" },
			{ title: "Company Name", id: "companyName" },
			{ title: "Company Website", id: "companyDomain" },
			{ title: "Company Linkedin", id: "companyLinkedinUrl" },
			{ title: "Company Country", id: "companyCountry" },
			{ title: "Save as Custom Field", id: "customField" },
		];
	}

	if (entityType === CsvEnrichmentType.PERSON) {
		return [
			{ title: "Do Not import", id: "" },
			{ title: "Email Address", id: "email" },
			{ title: "Job Title", id: "title" },
			{ title: "Name", id: "name" },
			{ title: "Firstname", id: "firstName" },
			{ title: "Lastname", id: "lastName" },
			{ title: "Company Name", id: "companyName" },
			{ title: "Company Website", id: "companyDomain" },
			{ title: "Personal Linkedin", id: "linkedinUrl" },
			{ title: "Company Linkedin", id: "companyLinkedinUrl" },
			{ title: "Company Country", id: "companyCountry" },
			{ title: "Save as Custom Field", id: "customField" },
		];
	}

	if (entityType === CsvEnrichmentType.PERSON_BY_TITLE) {
		return [
			{ title: "Do Not import", id: "" },
			{ title: "Job Title", id: "title" },
			{ title: "Company Name", id: "companyName" },
			{ title: "Company Website", id: "companyDomain" },
			{ title: "Company Linkedin", id: "companyLinkedinUrl" },
			{ title: "Company Country", id: "companyCountry" },
			{ title: "Personal Country", id: "personalCountry" },
			{ title: "Save as Custom Field", id: "customField" },
		];
	}

	return [];
};

/**
 * Field combinations that are sufficient to enrich a row — at least one of them must be mapped
 * before the mapping can be saved.
 */
export const minimumFieldsFor = (entityType?: List["entityType"]): string[][] => {
	if (entityType === CsvEnrichmentType.ORGANIZATION) {
		return [["companyName"], ["companyLinkedinUrl"], ["companyDomain"]];
	}

	if (entityType === CsvEnrichmentType.PERSON) {
		return [
			["linkedinUrl"],
			["firstName", "lastName", "companyName"],
			["firstName", "lastName", "companyLinkedinUrl"],
			["firstName", "lastName", "companyDomain"],
			["name", "companyName"],
			["name", "companyLinkedinUrl"],
			["name", "companyDomain"],
		];
	}

	if (entityType === CsvEnrichmentType.PERSON_BY_TITLE) {
		return [
			["title", "companyName"],
			["title", "companyLinkedinUrl"],
			["title", "companyDomain"],
		];
	}

	return [];
};

/** Column headers of a provided file, in file order. */
export const parseHeaders = (fileContent?: string): string[] =>
	parseCsvTable(fileContent).headers.filter(Boolean);

/** A few data rows, used to give the mapping suggestion something to look at. */
export const parseSampleRows = (fileContent?: string, limit = 3): string[][] =>
	parseCsvTable(fileContent).rows.slice(0, limit);

/**
 * The delivered file in the shape the title-screening gate expects (PREC-3536). That gate was built
 * for a SheetJS parse of a file being uploaded; here the file already exists and its content comes
 * back as CSV text, so it is adapted rather than re-parsed differently.
 */
export const toParsedXlsxData = (fileContent?: string): ParsedXlsxData | undefined => {
	const { headers, rows } = parseCsvTable(fileContent);

	if (!headers.length) {
		return undefined;
	}

	return { headers: headers.filter(Boolean), rows };
};

/** An empty mapping row per column — a file is always mapped from scratch (PREC-3091). */
export const emptyMapping = (headers: string[]): FieldMappingEntry[] =>
	headers.map((header) => ({ foundField: header, assignedField: "" }));

/** Applies a suggested mapping, ignoring any field the current entity type does not offer. */
export const applySuggestion = (
	headers: string[],
	suggestion: FieldMappingEntry[],
	options: MappingOption[]
): FieldMappingEntry[] => {
	const validIds = new Set(options.map((option) => option.id));

	return headers.map((header) => {
		const suggested = suggestion.find((entry) => entry.foundField.trim() === header)?.assignedField;

		return {
			foundField: header,
			assignedField: suggested && validIds.has(suggested) ? suggested : "",
		};
	});
};

/**
 * The file whose mapping the user works on: the newest one still waiting to be imported. Once
 * every file is imported there is nothing left to map, and the newest is shown read-only.
 */
export const pickMappingTarget = (csvEnrichments: CsvEnrichment[]): CsvEnrichment | undefined => {
	const byNewest = [...csvEnrichments].sort((a, b) => b.id - a.id);

	return byNewest.find((file) => !file.isImported) ?? byNewest[0];
};

/** The file delivered before the one being mapped — the baseline for "new column". */
export const findPreviousFile = (
	csvEnrichments: CsvEnrichment[],
	target?: CsvEnrichment
): CsvEnrichment | undefined =>
	[...csvEnrichments]
		.sort((a, b) => b.id - a.id)
		.find((file) => !!target && file.id !== target.id && file.id < target.id);

/**
 * Columns this file has that the previous one did not (PREC-3091). Purely informational — the
 * mapping is filled in from scratch either way — but it tells the user where to look.
 */
export const newColumnsOf = (headers: string[], previousHeaders: string[]): Set<string> => {
	if (!previousHeaders.length) {
		return new Set();
	}

	const known = new Set(previousHeaders.map((header) => header.toLowerCase()));

	return new Set(headers.filter((header) => !known.has(header.toLowerCase())));
};

/** Whether the mapping covers at least one combination that can enrich a row. */
export const meetsMinimumFields = (
	foundFields: FieldMappingEntry[],
	minimumFields: string[][]
): boolean => {
	const assigned = foundFields.map((field) => field.assignedField);

	return minimumFields.some((combination) =>
		combination.every((field) => assigned.includes(field))
	);
};
